import { useCallback, useContext, useEffect, useState } from "react";
import { RouteOverviewNavigatorView } from "./route-overview-navigator-view";
import { useAppContext } from "../../../utils/contexts/appContext";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Grid, makeStyles, TextField, Typography } from "@material-ui/core";
import Loader from "../../../components/loader";
import { AuthContext } from "../../../providers/AuthProvider";
import { convertToLocalRoutePlan } from "../../../utils/dexie/converters";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageContainer from "../../../components/page-container";
import { useApplicationParams } from "../../../hooks/app-params";
import { Redirect } from "react-router-dom";

const useStyles = makeStyles(theme => ({
    dayLabel: {
        textTransform: 'uppercase'
    }
}));

export const RouteOverviewNavigatorContainer = () => {
    const classes = useStyles();
    const [t,] = useTranslation();
    const [, dispatch] = useAppContext();
    const auth = useContext(AuthContext);
    const appParams = useApplicationParams();

    const [isLoading, setIsLoading] = useState(true);
    const [date, setDate] = useState(new Date());
    const [routePlans, setRoutePlans] = useState([]);

    const minDate = new Date();

    const loadData = useCallback(async () => {
        const result = await auth.fetch('get', `simulationsolutionroutes/planning/${date.toISOString()}`);
        if (result && result.status === 200) {
            const apiData = result.data;

            const convertedApiRoutePlans = apiData.map(apiRoutePlan => convertToLocalRoutePlan(apiRoutePlan, true));
            setRoutePlans(convertedApiRoutePlans);

            setIsLoading(false);
        }
    }, [auth, date]);

    useEffect(() => {
        dispatch({ 
            type: 'changeHeader', 
            header: { 
                title: t("Calendar"), 
                child: false 
            } 
        });

        if (navigator.onLine)
            loadData();
    }, [dispatch, t, loadData]);

    const adjustDate = (diff) => {
        setDate(prevDate => {
            const nextDate = new Date(prevDate);
            nextDate.setDate(nextDate.getDate() + diff);
            return nextDate;
        });
    };

    const formatDateForInput = (date) => {
        return date.toISOString().split('T')[0];
    };

    if (appParams?.calendarEnabled === false)
        return <Redirect to="/" />;

    return (
        <PageContainer>
            {
                isLoading ?
                    <Loader /> :
                    <Box mt={5}>
                        <Card elevation={2}>
                            <Grid container>
                                <Button onClick={() => adjustDate(-1)} disabled={date <= minDate}>
                                    <FontAwesomeIcon icon={['fad', 'chevron-double-left']} size="lg" />
                                </Button>
                                <Box flex={1} display="flex" alignItems="center" justifyContent="center" my={3}>
                                    <Grid container>
                                        <Grid container item xs={12} justifyContent="center">
                                            <Typography variant="h6" className={classes.dayLabel}>{new Intl.DateTimeFormat(navigator.language, { weekday: 'long' }).format(date)}</Typography>
                                        </Grid>
                                        <Grid container item xs={12} justifyContent="center">
                                            <TextField 
                                                type="date" 
                                                value={formatDateForInput(date)} 
                                                onKeyDown={e => e.preventDefault()}
                                                onChange={e => {
                                                    const value = e.target.value;
                                                    if (value !== '')
                                                        setDate(new Date(Date.parse(value)));
                                                }}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                inputProps={{
                                                    min: formatDateForInput(minDate)
                                                }} />
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Button onClick={() => adjustDate(1)}>
                                    <FontAwesomeIcon icon={['fad', 'chevron-double-right']} size="lg" />
                                </Button>
                            </Grid>
                        </Card>
                        <RouteOverviewNavigatorView routePlans={routePlans} />
                    </Box>
            }
        </PageContainer>
    );

}